import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import type { OnDocumentLoadSuccess, Options } from 'react-pdf/dist/shared/types.js';
import { cn, Stack } from '@prenuvo/halo-web';

import { setupPdfWorker } from '@/core/pdf-worker';

import 'react-pdf/dist/Page/TextLayer.css';

import { useWidth } from './use-width.hook';

setupPdfWorker();

interface PdfViewerProps {
  className?: string;
  error?: ReactNode;
  loading?: ReactNode;
  options?: Options;
  url: string;
}

export function PdfViewer({ className, error, loading, options, url }: PdfViewerProps) {
  const [numPages, setNumPages] = useState(0);
  const [isDocumentLoadSettled, setIsDocumentLoadSettled] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const width = useWidth(ref);

  useEffect(() => {
    setNumPages(0);
    setIsDocumentLoadSettled(false);
  }, [url]);

  const handleDocumentLoadSuccess: OnDocumentLoadSuccess = useCallback((doc) => {
    setNumPages(doc.numPages);
    setIsDocumentLoadSettled(true);
  }, []);

  return (
    <Stack
      className={cn('mx-auto w-full overflow-auto', className)}
      justification="center"
      ref={ref}
    >
      <Document
        key={url}
        error={error}
        file={url}
        loading={loading}
        onLoadError={() => setIsDocumentLoadSettled(true)}
        onLoadSuccess={handleDocumentLoadSuccess}
        options={options}
      >
        <div
          className={cn({
            'opacity-0': !isDocumentLoadSettled,
          })}
        >
          {Array.from(new Array(numPages), (_, index) => (
            <Page
              key={`page_${index + 1}`}
              className="w-full"
              pageNumber={index + 1}
              renderAnnotationLayer={false}
              width={width}
            />
          ))}
        </div>
      </Document>
    </Stack>
  );
}
